import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { configuration } from "src/configuration";
import { DEFAULT_ACTION_ITEM_STATUS } from "src/constants";
import { useSignalR } from "src/hooks";
import {
  ActionItemSummaryDto,
  ActionItemTeamPlanSectionDto,
  CreateActionItemDto,
  FetchError,
  GetPlanDto,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";
import { IUser } from "src/models";
import { lexorank } from "src/utilities";

type Variables = {
  teamPlanSection: ActionItemTeamPlanSectionDto;
  actionItemGuid: string;
  contextActionItemGuid: string | undefined;
  description: string;
  notes?: string;
  owners: IUser[];
  primaryOwner: IUser | null;
};

const useCreateActionItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createActionItemRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateActionItemDto = {
      actionItemGuid: variables.actionItemGuid,
      sectionGuid: variables.teamPlanSection.sectionGuid,
      contextActionItemGuid: variables.contextActionItemGuid,
      description: variables.description,
      notes: variables.notes,
      ownerUserIds: variables.owners?.map((o) => o.userId),
      primaryOwnerUserId: variables.primaryOwner?.userId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createActionItemRequest };
};

const useCreateActionItem = () => {
  const { createActionItemRequest } = useCreateActionItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createActionItemRequest,
    onMutate: async (variables) => {
      let newActionItem: ActionItemSummaryDto = {
        guid: variables.actionItemGuid,
        teamPlanSection: variables.teamPlanSection,
        description: variables.description,
        owners: variables.owners,
        currentStatus: DEFAULT_ACTION_ITEM_STATUS,
        statusLastModifiedDateTimeUtc: DateTime.now().toUTC().toString(),
        statusLastModifiedByDisplayName: "",
        note: variables.notes ?? "",
        dueDate: null,
        metricCount: 0,
        commentCount: 0,
        supportingItemsCount: 0,
        sortOrder: "",
        userCanEdit: true,
        createdDateTimeUtc: DateTime.now().toUTC().toString(),
        lastModifiedDateTimeUtc: DateTime.now().toUTC().toString(),
        hasSubitems: false,
        hasAzureDevOpsWorkItems: false,
        subitems: [],
      };

      const cachedPlanData = optimisticallyUpdateQueryData<GetPlanDto>(
        queryClient,
        queryKeys.plans.getPlan(variables.teamPlanSection.planGuid),
        (cachedPlan) => ({
          ...cachedPlan,
          blocks: cachedPlan.blocks.map((block) => ({
            ...block,
            sections: block.sections.map((section) =>
              section.guid === variables.teamPlanSection.sectionGuid
                ? {
                    ...section,
                    actionItems: [
                      ...section.actionItems,
                      {
                        ...newActionItem,
                        sortOrder: lexorank.getNextRank(section.actionItems),
                      },
                    ],
                  }
                : section,
            ),
          })),
        }),
      );

      queryClient.setQueryData(queryKeys.actionItems.summary(variables.actionItemGuid), newActionItem);

      return { cachedPlanData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.teamPlanSection.planGuid);

      if (variables.contextActionItemGuid) {
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
      }
    },
    onError: (_error, variables, context) => {
      if (context?.cachedPlanData) {
        queryClient.setQueryData(queryKeys.plans.getPlan(variables.teamPlanSection.planGuid), context.cachedPlanData);
      }

      queryClient.removeQueries(queryKeys.actionItems.summary(variables.actionItemGuid));
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.teamPlanSection.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.teamPlanSection.planGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.summary(variables.actionItemGuid));

      if (variables.contextActionItemGuid) {
        queryClient.invalidateQueries(queryKeys.actionItems.item(variables.contextActionItemGuid));
      }
    },
  });
};

export { useCreateActionItem };
